import React, { CSSProperties, useContext, useEffect } from "react";
import { useAppContext } from "../../app/AgTechAppContext";
import { logDev, logDevError } from "../../logging/AgTechLogger";
import { AgTechQueryRun } from "./AgTechDataQueries";

export type AgTechQueryLoadingSurfaceRetryConfiguration<TData> = {
    showRetryWhen: (data: TData) => { shouldRetry: boolean, retryMessage?: string },
    retryButtonText: string,
    retryButtonIcon?: string
}

export type AgTechQueryLoadingSurfaceSettings = {
    loading?: {
        showLoader?: boolean,
        showLoadingText?: boolean,
        loadingText?: string,
        loadingKey?: string
    },
    appearance?: {
        classes?: string,
        styles?: CSSProperties
    },
    errorHandling?: {
        showFailure?: boolean
    }
}

export type AgTechQueryLoadingSurfaceChildren<TQueryData> = (data: TQueryData, refresh: () => Promise<any>) => JSX.Element

export type AgTechQueryLoadingSurfaceProps<TQueryResponseData, TQueryProjectionData = TQueryResponseData> = AgTechQueryLoadingSurfaceSettings & {
    query?: AgTechQueryRun<TQueryResponseData>,
    projection?: (data: TQueryResponseData) => TQueryProjectionData,
    children: AgTechQueryLoadingSurfaceChildren<TQueryProjectionData>,
    hasFinishedLoading?: boolean,
    errorBoundary?: (props: { children: JSX.Element }) => JSX.Element,
    content?: AgTechQueryRun<TQueryResponseData>['content']
}

const AgTechQueryLoadingSurface = <TQueryResponseData, TQueryProjectionData = TQueryResponseData>(
    props: AgTechQueryLoadingSurfaceProps<TQueryResponseData, TQueryProjectionData>
): JSX.Element => {
    const appContext = useAppContext();
    const queryLoadingContext = useContext(AgTechQueryLoadingContextComponent);

    const [showContent, setShowContent] = React.useState(props.query?.queryResult?.data !== undefined);

    const query = props.query;

    const hasFinishedLoading = props.hasFinishedLoading ?? true;
    const hasQueryFailed = props.query?.queryResult.hasFailed ?? false;
    const hasQuerySucceeded = hasFinishedLoading && (props.query?.queryResult.hasSucceeded ?? true);
    const isQueryRefreshing = props.query?.queryResult.isRefreshing ?? false;
    
    const showLoadingText = props?.loading?.showLoadingText ?? true;
    const showFailure = props?.errorHandling?.showFailure ?? true;

    useEffect(() => {
        if (hasQuerySucceeded) {
            // If the given query is refreshing and content has not yet been shown,
            // hold off on showing content until the query has finished refreshing.
            if (isQueryRefreshing && !showContent) {
                return;
            }
            else {
                setShowContent(true);
            }
        }
        else if (hasQueryFailed && props.query) {
            logDevError('[Query Failed]', props.query.queryKey, props.query.queryResult.error);
        }
    }, [hasQuerySucceeded, hasQueryFailed, isQueryRefreshing]);
    
    if (hasQueryFailed && showFailure) {
        const requestErrorText = showLoadingText
            ? props.content?.failureText ?? props.query?.content?.failureText ?? 'An error occurred. Please restart the app.'
            : '';

        return (
            <>
                {appContext.loading.showLoadingError({
                    errorMessage: requestErrorText,
                    error: props.query?.queryResult.error,
                    retryConfiguration: props.query!.validation ? {
                        retryButtonText: props.query!.validation.retryButtonTextOnFailure ?? 'Retry',
                        retryIconText: props.query!.validation.retryButtonIconOnFailure ?? 'fas fa-rotate-right',
                        retryAction: async () => props.query!.queryResult.refresh()
                    } : undefined
                })}
            </>
        )
    }
    else if (showContent) {
        return (
            <QueryBodyMemo query={query} surfaceProps={props} />
        )
    }
    else {
        const showLoader = props?.loading?.showLoader ?? queryLoadingContext.showLoaders;

        const requestLoadingText = showLoadingText
            ? props.content?.loadingText ?? props.query?.content?.loadingText ?? 'Loading...'
            : '';

        return (
            <>
                {showLoader ? appContext.loading.showLoader(requestLoadingText) : undefined}
            </>
        );
    }
}

const QueryBody = <TQueryResponseData, TQueryProjectionData = TQueryResponseData>(props: {
    query?: AgTechQueryRun<TQueryResponseData>,
    surfaceProps: AgTechQueryLoadingSurfaceProps<TQueryResponseData, TQueryProjectionData>
}) => {
    const appContext = useAppContext();
    const queryData = props.query?.queryResult.data ?? {} as TQueryResponseData;

    const projectedData = props.surfaceProps.projection
        ? props.surfaceProps.projection(queryData)
        : queryData as unknown as TQueryProjectionData;

    const validationErrorMessage = props.query?.validation?.validate(queryData);

    return validationErrorMessage ? (
        <>
            {appContext.loading.showLoadingError({
                errorMessage: validationErrorMessage ?? 'Unable to load data',
                retryConfiguration: props.query?.validation ? {
                    retryButtonText: props.query?.validation?.retryButtonTextOnFailure ?? 'Retry',
                    retryIconText: props.query?.validation?.retryButtonIconOnFailure ?? 'bi bi-arrow-clockwise',
                    retryAction: async () => {
                        try
                        {
                            await props.query?.queryResult.refresh();
                        }
                        catch (e) {
                            console.error(e);
                        }
                    }
                } : undefined
            })}
        </>
    ) : props.surfaceProps.errorBoundary ? (
        <>
            {props.surfaceProps.errorBoundary({
                children: appContext.loading.showSurface({
                    children: props.surfaceProps.children(projectedData, async () => {
                        logDev('Refetching query', props.query?.queryKey);
                        await props.query?.queryResult.refresh();
                    }),
                    styles: props.surfaceProps?.appearance?.styles,
                    classes: props.surfaceProps?.appearance?.classes
                })
            })}
        </>
    ) : (
        <>
            {appContext.loading.showSurface({
                children: props.surfaceProps.children(projectedData, async () => {
                    logDev('Refetching query', props.query?.queryKey);
                    await props.query?.queryResult.refresh();
                }),
                styles: props.surfaceProps?.appearance?.styles,
                classes: props.surfaceProps?.appearance?.classes
            })}
        </>
    )
}

const QueryBodyMemo = React.memo(QueryBody, (prevProps, nextProps) => {
    const isRefreshing = prevProps.query?.queryResult.hasSucceeded && nextProps.query?.queryResult.isRefreshing;

    return isRefreshing ?? false;
}) as typeof QueryBody;

/// Context

type AgTechQueryLoadingContextType = {
    showLoaders: boolean
}

export const AgTechQueryLoadingContextComponent = React.createContext<AgTechQueryLoadingContextType>({
    showLoaders: true
});

export const AgTechQueryLoadingContext = (props: {
    showLoaders: boolean,
    children: JSX.Element
}) => {
    return (
        <AgTechQueryLoadingContextComponent.Provider value={{ showLoaders: props.showLoaders }}>
            {props.children}
        </AgTechQueryLoadingContextComponent.Provider>
    )
}

export default AgTechQueryLoadingSurface